import DOMPurify from 'dompurify';

/**
 * Product descriptions are merchant-authored HTML shown to shoppers, so every
 * value is run through DOMPurify before it reaches an editor or a storefront.
 *
 * The allowlist is deliberately narrow: formatting and links, no embedded
 * media, no ids or classes, no inline styles beyond text alignment. Anything
 * outside it is dropped rather than escaped, so a stray tag disappears instead
 * of showing up as literal angle brackets on the product page.
 */
const ALLOWED_TAGS = [
  'p',
  'br',
  'strong',
  'b',
  'em',
  'i',
  'u',
  's',
  'a',
  'ul',
  'ol',
  'li',
  'h2',
  'h3',
  'h4',
  'blockquote',
  'code',
  'pre',
  'span',
  'div',
];

const ALLOWED_ATTR = ['href', 'title', 'target', 'rel', 'style'];

/** Only text alignment survives; `style` is otherwise a hole big enough to hide in. */
const ALLOWED_STYLE = /^text-align:\s*(left|right|center|justify);?$/i;

let hooked = false;

/**
 * DOMPurify allows `style` wholesale or not at all, so the per-property filter
 * is a hook. Registered once, lazily - importing this module must not depend on
 * a DOM being present.
 */
function ensureHooks() {
  if (hooked) return;
  hooked = true;

  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (!(node instanceof Element)) return;

    const style = node.getAttribute('style');
    if (style !== null && !ALLOWED_STYLE.test(style.trim())) node.removeAttribute('style');

    // A link that opens a new tab must not hand the opener to the target.
    if (node.tagName === 'A' && node.getAttribute('target')) {
      node.setAttribute('rel', 'noopener noreferrer');
    }
  });
}

/** Sanitized HTML, safe to hand to `dangerouslySetInnerHTML`. */
export function sanitizeRichText(html: string): string {
  ensureHooks();
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    // `javascript:` and friends never survive; relative and anchor links do.
    ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
  });
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Descriptions written before the editor existed - and the ones the AI drafts -
 * are plain text with newlines. Left alone they would collapse onto one line
 * the moment they were rendered as HTML, so they are promoted to paragraphs.
 */
export function toRichTextHtml(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (/<[a-z][\s\S]*>/i.test(trimmed)) return sanitizeRichText(trimmed);

  return trimmed
    .split(/\n{2,}/)
    .map((block) => `<p>${escapeHtml(block).replace(/\n/g, '<br>')}</p>`)
    .join('');
}

/**
 * Whether the markup carries no actual content. An emptied editor leaves
 * scaffolding like `<p><br></p>` behind, which must be stored as nothing at
 * all or the product page would render a blank Description heading.
 */
export function isRichTextEmpty(html: string): boolean {
  if (!html.trim()) return true;
  return !html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .trim();
}

/** Whether a stored value is markup (from the editor) rather than plain text written before it. */
export function looksLikeHtml(value: string): boolean {
  return /<[a-z][\s\S]*>/i.test(value);
}

const BLOCK_END = /<\/(p|div|h[1-6]|li|blockquote|pre|ul|ol)>|<br\s*\/?>/gi;

/**
 * Markup as the words a search engine or a meta description wants: tags
 * gone, blocks on their own lines, entities decoded. Plain text passes
 * through with only its runs of blank lines tidied.
 */
export function richTextToPlain(value: string): string {
  if (!looksLikeHtml(value)) return value.replace(/\n{3,}/g, '\n\n').trim();
  const marked = value.replace(BLOCK_END, (tag) => `${tag}\n`);
  let text: string;
  if (typeof DOMParser !== 'undefined') {
    // Sanitized first, so a script's or a style's own text is not read out
    // as words; then a parsed, never-attached document, where nothing loads.
    text = new DOMParser().parseFromString(sanitizeRichText(marked), 'text/html').body.textContent ?? '';
  } else {
    text = marked
      .replace(/<(script|style)[\s\S]*?<\/\1>/gi, '')
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/gi, ' ')
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>')
      .replace(/&quot;/gi, '"')
      .replace(/&#39;/gi, "'")
      .replace(/&amp;/gi, '&');
  }
  return text
    .replace(/\u00a0/g, ' ')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}
